import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, CheckCircle2, XCircle, Pencil, ExternalLink, Inbox, MessageSquare, Loader2, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QILogo } from "@/components/site/QILogo";
import { toast } from "sonner";
import { PartnerDashboard } from "@/components/site/PartnerDashboard";

const STATUS = {
  pending: { icon: Clock, cls: "text-qi-gold bg-qi-gold/10 border-qi-gold/30", label: "Pending review", note: "Our team is reviewing your listing. You'll appear in the directory once approved." },
  approved: { icon: CheckCircle2, cls: "text-qi-emerald bg-qi-emerald/10 border-qi-emerald/30", label: "Live in directory", note: "Your listing is public and investors can contact you." },
  rejected: { icon: XCircle, cls: "text-red-400 bg-red-500/10 border-red-500/30", label: "Not approved", note: "Your listing wasn't approved. Update your details and contact support." },
};

const ProfileCard = ({ profile }) => {
  const s = STATUS[profile.status];
  return (
    <div className="glass-card p-7 flex flex-col md:flex-row md:items-center gap-6" data-testid="dashboard-profile-card">
      <QILogo profile={profile} size="w-20 h-20" text="text-2xl" />
      <div className="flex-1 min-w-0">
        <h2 className="font-display text-2xl font-black">{profile.company_name}</h2>
        <span data-testid="dashboard-listing-status" className={`inline-flex items-center gap-1.5 mt-2 text-xs font-bold rounded-full px-3 py-1 border ${s.cls}`}><s.icon size={13} />{s.label}</span>
        {profile.is_founding && <span className="ml-2 inline-flex items-center gap-1 text-xs font-bold rounded-full px-3 py-1 bg-qi-pink/15 text-qi-pink"><Sparkles size={12} />Founding member · free for life</span>}
        <p className="text-sm text-white/50 mt-3">{s.note}</p>
      </div>
      <div className="flex gap-3">
        <Link to="/intake" className="btn-ghost !py-2.5 text-sm" data-testid="dashboard-edit-profile-button"><Pencil size={14} /> Edit</Link>
        {profile.status === "approved" && <Link to={`/qi/${profile.slug}`} className="btn-pink !py-2.5 text-sm" data-testid="dashboard-view-listing-button"><ExternalLink size={14} /> View</Link>}
      </div>
    </div>
  );
};

const LeadRow = ({ lead, onStatus }) => (
  <div className="p-5 border-b border-white/5 last:border-0" data-testid={`lead-row-${lead.lead_id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-bold">{lead.name} {lead.status === "new" && <span className="ml-2 text-[10px] bg-qi-emerald text-ink font-black rounded-full px-2 py-0.5">NEW</span>}
          {lead.source === "referral" && <span data-testid="lead-referral-badge" className="ml-2 text-[10px] bg-qi-pink/15 text-qi-pink font-black rounded-full px-2 py-0.5">REFERRAL · {lead.referrer?.firm} ({lead.referrer?.profession})</span>}</p>
        <p className="text-xs text-white/50 mt-1">
          <a href={`mailto:${lead.email}`} className="hover:text-qi-pink">{lead.email}</a>{lead.phone && ` · ${lead.phone}`} · {new Date(lead.created_at).toLocaleDateString()}
        </p>
      </div>
      <select value={lead.status} onChange={(e) => onStatus(lead.lead_id, e.target.value)} className="field !w-auto !py-2 !text-xs" data-testid={`lead-status-select-${lead.lead_id}`}>
        <option value="new" className="bg-ink">New</option>
        <option value="contacted" className="bg-ink">Contacted</option>
        <option value="closed" className="bg-ink">Closed</option>
      </select>
    </div>
    <div className="flex flex-wrap gap-2 mt-3">
      {lead.property_value && <span className="text-[11px] font-mono bg-white/5 rounded-full px-2.5 py-1">{lead.property_value}</span>}
      {lead.timeline && <span className="text-[11px] font-mono bg-white/5 rounded-full px-2.5 py-1">{lead.timeline}</span>}
    </div>
    <p className="text-sm text-white/70 mt-3 whitespace-pre-line">{lead.message}</p>
  </div>
);

const Leads = () => {
  const [leads, setLeads] = useState(null);
  useEffect(() => {
    api.get("/me/leads").then(({ data }) => setLeads(data)).catch(() => setLeads([]));
  }, []);
  const onStatus = async (id, status) => {
    try {
      await api.patch(`/me/leads/${id}`, { status });
      setLeads(leads.map((l) => (l.lead_id === id ? { ...l, status } : l)));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="glass-card overflow-hidden" data-testid="dashboard-leads">
      <div className="p-6 border-b border-white/10 flex items-center gap-3"><Inbox size={18} className="text-qi-pink" /><h3 className="font-display text-lg font-bold">Investor & referral inbox</h3>
        <span className="font-mono text-xs text-white/40 ml-auto">{leads?.length || 0} total</span></div>
      {leads === null ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>
        : leads.length === 0 ? <p className="p-10 text-center text-sm text-white/40" data-testid="dashboard-leads-empty">No inquiries yet. They'll appear here once investors contact you.</p>
          : leads.map((l) => <LeadRow key={l.lead_id} lead={l} onStatus={onStatus} />)}
    </div>
  );
};

const ChooseType = () => (
  <div className="grid md:grid-cols-2 gap-6" data-testid="dashboard-no-profile">
    <div className="relative overflow-hidden glass-card p-8">
      <div className="absolute -top-20 -right-20 w-72 h-72 bg-qi-pink/20 rounded-full blur-3xl" />
      <h2 className="relative font-display text-2xl font-black">I'm a Qualified Intermediary</h2>
      <p className="relative text-white/55 mt-2">Complete the intake to claim a free founding listing and unlock investor leads and the network chat.</p>
      <Link to="/intake" className="relative btn-pink mt-6" data-testid="dashboard-start-intake-button">Start QI intake</Link>
    </div>
    <div className="glass-card p-8">
      <h2 className="font-display text-2xl font-black">I'm a referral partner</h2>
      <p className="text-white/55 mt-2">CPA, attorney, broker or advisor? Join the 1031 Club free to chat with QIs and send them client referrals.</p>
      <Link to="/partner-intake" className="btn-ghost mt-6" data-testid="dashboard-start-partner-button">Join as a partner</Link>
    </div>
  </div>
);

export default function Dashboard() {
  const { user, profile, partner } = useAuth();
  if (profile === null || partner === null) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>;
  if (partner) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 space-y-6" data-testid="dashboard-page">
        <div><span className="eyebrow">Dashboard</span><h1 className="font-display text-4xl font-black tracking-tight mt-3">Welcome, {user.name?.split(" ")[0]}</h1></div>
        <PartnerDashboard partner={partner} />
      </div>
    );
  }
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 space-y-6" data-testid="dashboard-page">
      <div>
        <span className="eyebrow">Dashboard</span>
        <h1 className="font-display text-4xl font-black tracking-tight mt-3">Welcome, {user.name?.split(" ")[0]}</h1>
      </div>
      {profile ? <ProfileCard profile={profile} /> : <ChooseType />}
      {profile && <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><Leads /></div>
        <Link to="/chat" className="glass-card p-7 h-fit hover:border-qi-pink/40 transition-colors" data-testid="dashboard-chat-card">
          <MessageSquare className="text-qi-pink" />
          <h3 className="font-display text-lg font-bold mt-4">1031 Club chat</h3>
          <p className="text-sm text-white/50 mt-2">Message other intermediaries and referral partners. Start a chat from any QI's profile or the <span className="text-qi-pink">Partners</span> page.</p>
        </Link>
      </div>}
    </div>
  );
}
